import type { CSSProperties, MouseEvent, ReactNode } from "react";
import { Link } from "react-router-dom";
import { courseScrimColor } from "./courseScrim";
import { textColorsForBackground } from "./lessonCardColors";

type CourseCardProps = {
  readonly title: string;
  readonly description?: string | null;
  readonly heroImageUrl?: string | null;
  readonly accentColor?: string | null;
  readonly statsLine?: string;
  readonly badges?: ReactNode;
  readonly detailsHref?: string;
  readonly manageHref?: string;
  readonly onOpenDetails?: () => void;
  /** Replaces Details/Manage (e.g. community “copy” action). */
  readonly footerActions?: ReactNode;
};

/**
 * Course tile with a hero band and a body that always shows title, stats, and description.
 */
export function CourseCard({
  title,
  description,
  heroImageUrl,
  accentColor,
  statsLine,
  badges,
  detailsHref,
  manageHref,
  onOpenDetails,
  footerActions,
}: CourseCardProps) {
  const accent = accentColor?.trim() || null;
  const hasImage = Boolean(heroImageUrl);
  const scrim = courseScrimColor(accentColor);
  const mediaColors = accent && !hasImage ? textColorsForBackground(accent) : null;

  const openDetails = (e: MouseEvent) => {
    e.stopPropagation();
    onOpenDetails?.();
  };

  const stopNav = (e: MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <article
      className="teacher-course-card"
      data-has-accent={accent ? "" : undefined}
      data-has-image={hasImage ? "" : undefined}
      data-tone={mediaColors?.tone}
      style={
        {
          "--course-card-scrim": scrim,
          ...(accent && !hasImage ? { "--course-card-bg": accent } : {}),
        } as CSSProperties
      }
    >
      <div className="teacher-course-card-media">
        {badges ? <div className="teacher-course-card-badges">{badges}</div> : null}
        {heroImageUrl ? (
          <img className="teacher-course-card-img" src={heroImageUrl} alt="" loading="lazy" crossOrigin="anonymous" />
        ) : null}
        {hasImage ? <div className="teacher-course-card-scrim" aria-hidden /> : null}
        {!hasImage ? (
          <h3
            className="teacher-course-card-media-title"
            style={mediaColors ? { color: mediaColors.title } : undefined}
          >
            {title}
          </h3>
        ) : null}
        {onOpenDetails ? (
          <button
            type="button"
            className="teacher-course-card-hit"
            onClick={openDetails}
            aria-label={`View details for ${title}`}
          />
        ) : null}
      </div>
      <div className="teacher-course-card-body">
        {hasImage ? <h3 className="teacher-course-card-title">{title}</h3> : null}
        {statsLine ? <p className="teacher-course-card-stats">{statsLine}</p> : null}
        <p className="teacher-course-card-desc">{description || "No description yet."}</p>
        <div className="teacher-course-card-actions">
          {footerActions ?? (
            <>
              {detailsHref ? (
                <Link to={detailsHref} className="btn primary tight" style={{ textDecoration: "none" }} onClick={stopNav}>
                  Details
                </Link>
              ) : null}
              {manageHref ? (
                <Link to={manageHref} className="btn secondary tight" style={{ textDecoration: "none" }} onClick={stopNav}>
                  Manage
                </Link>
              ) : null}
            </>
          )}
        </div>
      </div>
    </article>
  );
}
